import { memo, useCallback, useEffect, useState } from "react";
import {
  DesktopSATechValuesFilter,
  MobileSATechValuesFilter,
} from "~/features/selectionAssistant/bearing/components/Filters/TechFilters/SATechValuesFilter";
import classNames from "classnames";
import { CharacteristicsFilter } from "~/features/selectionAssistant/bearing/components/Filters/CharacteristicsFilter";
import { SATaxonomyFilter } from "~/features/selectionAssistant/bearing/components/Filters/SATaxonomyFilter";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { AccordionGroup, AccordionGroupTitle } from "~/common/mui/Accordion/AccordionGroup";
import { Card } from "~/features/configurator/Linear/components/Card";
import { changeSATab } from "~/features/selectionAssistant/bearing/redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { applyAllFiltersAction } from "~/features/search/redux/actions";
import { selectAppliedTechFilterValues } from "~/features/search/redux/searchSelectors";
import {
  flattenTechFilterFormData,
  unflattenTechFilterFormData,
} from "~/features/search/utils/flattenTechFilterFormData";
import { TechFilterResetContext } from "~/features/search/components/Filters/TechFilterResetContext";

import classes from "~/features/selectionAssistant/bearing/components/Filters/SAFilters.module.less";
import { Accordion, AccordionDetails, AccordionSummary, Tab, TabContext, TabList, TabPanel } from "~/common/mui";

export const SAFilters = memo(() => {
  const isDesktop = useIsMinDesktopSize();
  const appliedTechFilterValues = useSelector(selectAppliedTechFilterValues);
  const dispatch = useDispatch();

  const methods = useForm({
    values: unflattenTechFilterFormData(appliedTechFilterValues),
    mode: "onChange",
  });

  useEffect(() => {
    methods.reset();
  }, [appliedTechFilterValues, methods]);

  const onSubmit = (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => {
    const flattenedData = flattenTechFilterFormData(data);
    dispatch(applyAllFiltersAction(flattenedData));
  };

  const resetFilters = useCallback(
    (filterUids?: string[]) => {
      const currentValues = methods.getValues();

      // If specific UIDs provided, clear only those. Otherwise, get all tech filter UIDs from current values
      const uidsToReset = filterUids || Object.keys(currentValues);

      uidsToReset.forEach((uid) => {
        methods.setValue(`${uid}.min`, undefined, { shouldDirty: true, shouldTouch: true });
        methods.setValue(`${uid}.max`, undefined, { shouldDirty: true, shouldTouch: true });
      });

      // Submit the form to trigger the search
      methods.handleSubmit(onSubmit)();
    },
    [methods, onSubmit],
  );

  return (
    <TechFilterResetContext.Provider value={{ resetFilters }}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>{isDesktop ? <DesktopSAFilters /> : <MobileSAFilters />}</form>
      </FormProvider>
    </TechFilterResetContext.Provider>
  );
});

const MOBILE_FILTER_CONFIG = [
  {
    title: "page.selectionAssistant.characteristic&type",
    elements: [
      {
        title: "page.selectionAssistant.tab.characteristics",
        content: <CharacteristicsFilter />,
      },
      {
        title: "page.selectionAssistant.tab.bearing",
        content: <SATaxonomyFilter />,
      },
    ],
  },
  {
    title: "page.selectionAssistant.dimensions&loadrating",
    elements: [
      {
        title: "page.selectionAssistant.tab.dimensions",
        content: <MobileSATechValuesFilter index={0} />,
      },
      {
        title: "page.selectionAssistant.tab.loadRating",
        content: <MobileSATechValuesFilter index={1} />,
      },
    ],
  },
];

const MobileSAFilters = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <div className={classes.mobileFilters}>
      <div className={"fullbleed"}>
        {MOBILE_FILTER_CONFIG.map((group, groupIndex) => (
          <AccordionGroup key={groupIndex}>
            <AccordionGroupTitle>{t(group.title)}</AccordionGroupTitle>
            {group.elements.map((element, elementIndex) => (
              <Accordion key={elementIndex}>
                <AccordionSummary>{t(element.title)}</AccordionSummary>
                <AccordionDetails>{element.content}</AccordionDetails>
              </Accordion>
            ))}
          </AccordionGroup>
        ))}
      </div>
      {/*<ResetAllLinkBtn onClick={() => dispatch(resetSAFilters())} />*/}
    </div>
  );
};

const DesktopSAFilters = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("Characteristics");
  const dispatch = useDispatch();

  const changeTab = (val: string) => {
    setActiveTab(val);
    dispatch(changeSATab(val));
  };

  return (
    <TabContext value={activeTab}>
      <TabList onChange={(e, val) => changeTab(val)} variant={"standard"}>
        <Tab
          data-test-id={"selectionassistant-characteristic-type"}
          value={"Characteristics"}
          label={t("page.selectionAssistant.characteristic&type")}
        />
        <Tab
          data-test-id={"selectionassistant-dimensions-loadrating"}
          value={"Dimensions"}
          label={t("page.selectionAssistant.dimensions&loadrating")}
        />
      </TabList>

      <TabPanel value={"Characteristics"} keepMounted>
        {/*<div className={classes.characteristicsTabRoot}>*/}
        <div className={classes.csticTabGrid}>
          <div className={classes.csticTabCardTitle}>
            <Title>{t("page.selectionAssistant.characteristicsProperties")}</Title>
            <Description>{t("page.selectionAssistant.info.characteristicsProperties")}</Description>
          </div>

          <Card disableHover={true} className={classes.csticTabCard}>
            <CharacteristicsFilter />
          </Card>

          <div className={classes.csticTabCardTitle}>
            <Title>{t("page.selectionAssistant.bearingType")}</Title>
            <Description>{t("page.selectionAssistant.info.bearingType")}</Description>
          </div>

          <Card disableHover={true} className={classes.csticTabCard}>
            <SATaxonomyFilter />
          </Card>
        </div>

        {/*<ResetAllBtn onClick={() => dispatch(resetSAFilters())} />*/}
        {/*</div>*/}
      </TabPanel>

      <TabPanel value={"Dimensions"} keepMounted>
        <DesktopSATechValuesFilter />
      </TabPanel>
    </TabContext>
  );
};

const Title = ({ className, ...rest }: JSX.IntrinsicElements["h3"]) => (
  <h3 className={classNames("scha__sa__section__title", className)} {...rest} />
);
const Description = ({ className, ...rest }: JSX.IntrinsicElements["div"]) => (
  <div className={classNames("scha__sa__section__description", className)} {...rest} />
);
